import React from 'react';
import { Form, Modal, Checkbox, message, Button } from 'antd';
import s from './s.module.less';
import dayjs from 'utils/dayjs';
import commonS from 'styles/common.module.less';
import { FormReply } from './types';
import { getTimeStr } from 'utils/calendar';
import { EReviewSource, TReviewItem } from '../../types';
import StarReview from 'components/StarReview';
import ProviderStore from 'store/Provider';
import AIRichText from 'components/AIRichText';
import { EAIRichTextEditorType } from 'types/ai';
import { getProviderNameDisplay } from 'utils/provider';
import { addKlarityReviewReply } from 'api/survey';
import { addPracticeReviewReply } from 'api/group';
import { TKlarityReviewReplyInput } from 'types/survey';

type Props = {
    show: boolean,
    item?: TReviewItem,
    isPracticeView?: boolean,
    onCannel?: () => void,
    onSuccess?: () => void
};

const ReplyReviewModal = (props: Props) => {
    const { show, item, isPracticeView, onCannel, onSuccess } = props;
    const [submiting, setSubmiting] = React.useState(false);
    const [getUser] = ProviderStore.useStore();
    const user = getUser('data');
    const [form] = Form.useForm<FormReply>();
    // 是否在 helloklarity.com 上展示回复（仅针对 public review）
    const [showOn, setShowOn] = React.useState(true);
    // 是否同时作为 direct message 发送给患者
    const [sendAsDirectMessage, setSendAsDirectMessage] = React.useState(false);
    const [showPhiLeakageModal, setShowPhiLeakageModal] = React.useState(false);
    const [showInappropriateContentModal, setInappropriateContentModal] = React.useState(false);

    const verified = item?.source !== EReviewSource.PROVIDER_IMPORT;

    // 将 HTML 内容转换为纯文本
    const htmlToPlainText = (html: string): string => {
        if (!html) return '';
        // 创建一个临时 DOM 元素来解析 HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        // 获取纯文本内容，并处理换行
        let text = tempDiv.textContent || tempDiv.innerText || '';
        // 清理多余的空白字符，但保留换行
        text = text.replace(/\s+/g, ' ').trim();
        return text;
    };

    React.useEffect(() => {
        if (!show) {
            form.resetFields();
            setShowOn(true);
            setSendAsDirectMessage(false);
        } else {
            // 如果评论是 Positive (rating >= 4) 且 Public，则预填充文本
            const isPositive = item?.rating && item.rating >= 4;
            const isPublic = item?.isPublic === true;
            // 如果评论不是 public，则回复也不能是 public
            if (!isPublic) {
                setShowOn(false);
            } else {
                setShowOn(true);
            }
            // 如果评论不是匿名，并且有联系方式，则默认勾选「Send this reply as a direct message」
            const canSendDirect =
                !item?.isAnonymous &&
                (!!item?.reviewerEmail || !!item?.reviewerPhone);
            setSendAsDirectMessage(!!canSendDirect);
            if (isPositive && isPublic) {
                const prefilledText = 'Thank you for the kind words. We appreciate your trust and will keep working to provide timely, evidence-based care.';
                form.setFieldsValue({ content: prefilledText });
            } else {
                // 其他情况设置为空
                form.setFieldsValue({ content: '' });
            }
        }
    }, [show, form, item?.rating, item?.isPublic, item?.isAnonymous, item?.reviewerEmail, item?.reviewerPhone]);

    // 判断是否超过30天回复期限
    const isReplyPeriodExpired = () => {
        if (!item?.submitAt) return false;
        const submitDate = dayjs(item.submitAt);
        const now = dayjs();
        const daysDiff = now.diff(submitDate, 'day');
        return daysDiff > 30;
    };

    const onSubmit = async (isConfirmPhiLeakage?: boolean, forcePrivate?: boolean): Promise<void> => {
        // 检查是否超过30天回复期限
        if (isReplyPeriodExpired()) {
            message.error('The 30-day reply period has expired. You cannot reply to this review.');
            return;
        }

        setSubmiting(true);
        try {
            const formValues = (await form.validateFields()) as FormReply;

            // 确保 subject 有默认值
            const defaultSubject = `Message from ${getProviderNameDisplay(user)} regarding your feedback on Klarity`;

            // 将富文本内容转换为纯文本
            const plainTextContent = htmlToPlainText(formValues.content || '');

            const data: TKlarityReviewReplyInput = {
                ...formValues,
                content: plainTextContent, // 使用转换后的纯文本
                subject: formValues.subject || defaultSubject,
                reviewId: item?.id!,
                isPublic: showOn,
                isConfirmPhiLeakage: !!isConfirmPhiLeakage,
                sendAsDirectMessage,
                isSendDirectMessage: sendAsDirectMessage,
            };
            // 如果评论不是 public，回复也不能是 public
            if (item?.isPublic !== true) {
                data.isPublic = false;
            }
            if (forcePrivate) {
                data.isPublic = false;
            }
            const replyFn = isPracticeView ? addPracticeReviewReply : addKlarityReviewReply;
            const result = await replyFn(data);
            if (!result.error) {
                message.success('Reply sent successfully');
                if (onSuccess) {
                    onSuccess();
                }
            } else if (`${result.errorCode}` === '90001') {
                setShowPhiLeakageModal(true);
            } else if (`${result.errorCode}` === '90002') {
                setInappropriateContentModal(true);
            } else {
                message.error(result?.error || '');
            }
        } catch (e: unknown) {
            const err = e as { errorFields?: unknown };
            if (err.errorFields) {
                //form error
                return;
            }
            //const msg = e?.toString?.() || 'data error';
            //message.error(msg);
        } finally {
            setSubmiting(false);
        }
    };

    const hasTag = !!item?.tags?.trim();

    return (
        <>
            <Modal
                title="Reply review"
                closable
                className={commonS.modalFixHeightWrap}
                width="1200px"
                style={{
                    maxWidth: '94%',
                }}
                onCancel={() => {
                    onCannel?.();
                }}
                okText="Send"
                open={show}
                onOk={() => {
                    onSubmit();
                }}
                destroyOnClose
                okButtonProps={{ loading: submiting }}
                cancelButtonProps={{ style: { display: 'none' } }}
            >
                <div className={s.wrap}>
                    <div className={s.left}>
                        <div className={s.feedbackItem} key={`${item?.id}`}>
                            {!verified && (
                                <>
                                    <div className={s.name}>
                                        {/* Provider Import 显示 Anonymous，不再展示 source 文案 */}
                                        {item?.isAnonymous || item?.source === EReviewSource.PROVIDER_IMPORT
                                            ? 'Anonymous'
                                            : 'Verified patient'}
                                    </div>
                                    {/* <div className={s.contacts}>
                                        {hasReviewEmail && (
                                            <div className={s.infoWrap}>
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="16"
                                                    height="16"
                                                    viewBox="0 0 16 16"
                                                    fill="none"
                                                >
                                                    <path
                                                        d="M2 5.33398L7.2604 8.84092C7.70827 9.1395 8.29173 9.1395 8.7396 8.84092L14 5.33398M3.33333 12.6673H12.6667C13.403 12.6673 14 12.0704 14 11.334V4.66732C14 3.93094 13.403 3.33398 12.6667 3.33398H3.33333C2.59695 3.33398 2 3.93094 2 4.66732V11.334C2 12.0704 2.59695 12.6673 3.33333 12.6673Z"
                                                        stroke="#9CA3AF"
                                                        strokeWidth="1.33333"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                </svg>
                                                <span className={s.info}>
                                                    {item?.reviewerEmail}
                                                </span>
                                            </div>
                                        )}
                                        {hasReviewPhone && (
                                            <div className={s.infoWrap}>
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="16"
                                                    height="16"
                                                    viewBox="0 0 16 16"
                                                    fill="none"
                                                >
                                                    <path
                                                        d="M6.15195 2.45585L5.51949 2.66667V2.66667L6.15195 2.45585ZM7.15049 5.45147L7.78295 5.24066L7.78295 5.24066L7.15049 5.45147ZM6.81618 6.25858L6.51804 5.66229L6.81618 6.25858ZM5.31134 7.011L5.0132 6.41471C4.69317 6.57473 4.55652 6.95883 4.70359 7.28501L5.31134 7.011ZM8.989 10.6887L8.71499 11.2964C9.04117 11.4435 9.42527 11.3068 9.58529 10.9868L8.989 10.6887ZM9.74142 9.18382L9.14514 8.88568L9.14514 8.88568L9.74142 9.18382ZM10.5485 8.84951L10.7593 8.21705L10.5485 8.84951ZM13.5442 9.84805L13.3333 10.4805L13.5442 9.84805ZM3.33333 1.33333C2.22876 1.33333 1.33333 2.22876 1.33333 3.33333H2.66667C2.66667 2.96514 2.96514 2.66667 3.33333 2.66667V1.33333ZM5.51949 1.33333H3.33333V2.66667H5.51949V1.33333ZM6.78441 2.24503C6.60292 1.70057 6.0934 1.33333 5.51949 1.33333V2.66667V2.66667L6.78441 2.24503ZM7.78295 5.24066L6.7844 2.24503L5.51949 2.66667L6.51804 5.66229L7.78295 5.24066ZM7.11432 6.85486C7.7078 6.55812 7.99277 5.87014 7.78295 5.24066L6.51804 5.66229V5.66229L7.11432 6.85486ZM5.60948 7.60728L7.11432 6.85486L6.51804 5.66229L5.0132 6.41471L5.60948 7.60728ZM9.26302 10.0809C7.78142 9.41291 6.58709 8.21858 5.91909 6.73698L4.70359 7.28501C5.50524 9.06305 6.93695 10.4948 8.71499 11.2964L9.26302 10.0809ZM9.14514 8.88568L8.39272 10.3905L9.58529 10.9868L10.3377 9.48196L9.14514 8.88568ZM10.7593 8.21705C10.1299 8.00723 9.44188 8.2922 9.14514 8.88568L10.3377 9.48197V9.48196L10.7593 8.21705ZM13.755 9.2156L10.7593 8.21705L10.3377 9.48196L13.3333 10.4805L13.755 9.2156ZM14.6667 10.4805C14.6667 9.9066 14.2994 9.39708 13.755 9.2156L13.3333 10.4805H13.3333H14.6667ZM14.6667 12.6667V10.4805H13.3333V12.6667H14.6667ZM12.6667 14.6667C13.7712 14.6667 14.6667 13.7712 14.6667 12.6667H13.3333C13.3333 13.0349 13.0349 13.3333 12.6667 13.3333V14.6667ZM12 14.6667H12.6667V13.3333H12V14.6667ZM1.33333 4C1.33333 9.89104 6.10896 14.6667 12 14.6667V13.3333C6.84534 13.3333 2.66667 9.15466 2.66667 4H1.33333ZM1.33333 3.33333V4H2.66667V3.33333H1.33333Z"
                                                        fill="#9CA3AF"
                                                    />
                                                </svg>
                                                <span className={s.info}>
                                                    {item?.reviewerPhone}
                                                </span>
                                            </div>
                                        )}
                                    </div> */}
                                </>
                            )}
                            {verified && (
                                <div className={s.name}>
                                    {/* isAnonymous 或 isNeedReplied 为 false 时不展示 reviewerName，只展示通用文案 */}
                                    {item?.isAnonymous || !item?.isNeedReplied
                                        ? 'Verified patient'
                                        : (item?.reviewerName || 'Verified patient')}
                                </div>
                            )}
                            <div className={s.scoreInfo}>
                                <StarReview
                                    width={120}
                                    editable={false}
                                    value={item?.rating}
                                />
                                <div className={s.time}>
                                    {getTimeStr(
                                        dayjs(item?.submitAt),
                                        'MMMM D, YYYY',
                                    )}
                                </div>
                                {!item?.isPublic && (
                                    <span className={s.private}>Private</span>
                                )}
                                {item?.isPublic && (
                                    <span className={s.public}>Public</span>
                                )}
                            </div>
                            {hasTag && (
                                <div className={s.tagList}>
                                    {item?.tags
                                        ?.split(',')
                                        .map((tag: string) => {
                                            return (
                                                <div
                                                    key={tag}
                                                    className={s.tag}
                                                >
                                                    {tag}
                                                </div>
                                            );
                                        })}
                                </div>
                            )}
                            <div className={s.content}>
                                {item?.comment?.content}
                            </div>
                        </div>
                        <Form
                            form={form}
                            name="basic"
                            className={commonS.formStyle1}
                            initialValues={{
                                subject: `Message from ${getProviderNameDisplay(
                                    user,
                                )} regarding your feedback on Klarity`,
                            }}
                            autoComplete="off"
                            layout="vertical"
                            scrollToFirstError
                        >
                            <div className={s.modalWrap}>
                                {/* <div className={s.row}>
                                    <Form.Item
                                        style={{ width: '100%', maxWidth: '700px' }}
                                        label="Subject"
                                        name="subject"
                                        rules={[
                                            { required: true, message: 'Subject is required' },
                                            { max: 100, message: 'Subject maximum character length of 100' },
                                        ]}
                                    >
                                        <Input maxLength={100} placeholder="" size="large" />
                                    </Form.Item>
                                </div> */}
                                {/*
                            <div className={s.maxTips}>Max. 3000 characters</div> */}
                                {/* <div className={s.row} style={{ marginTop: '-24px' }}> */}
                                <div className={s.row}>
                                    <Form.Item
                                        style={{
                                            width: '100%',
                                            maxWidth: '700px',
                                        }}
                                        label="Reply"
                                        name="content"
                                        rules={[
                                            {
                                                required: true,
                                                message: 'Reply is required',
                                            },
                                            {
                                                validator: (_, value) => {
                                                    if (!value) {
                                                        return Promise.resolve();
                                                    }
                                                    // 计算纯文本长度（去除 HTML 标签）
                                                    const textContent = value.replace(/<[^>]*>/g, '').trim();
                                                    if (textContent.length > 3000) {
                                                        return Promise.reject(new Error('Reply maximum character length of 3000'));
                                                    }
                                                    return Promise.resolve();
                                                },
                                            },
                                        ]}
                                    >
                                        <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.content !== currentValues.content}>
                                            {({ getFieldValue }) => {
                                                const content = getFieldValue('content') || '';
                                                // 计算纯文本长度（去除 HTML 标签）
                                                const textLength = content.replace(/<[^>]*>/g, '').trim().length;
                                                return (
                                                    <div className={s.replyEditorWrap}>
                                                        {/* <div className={s.replyHeader}>Hello,</div> */}
                                                        <AIRichText
                                                            value={content}
                                                            onChange={(val) => {
                                                                form.setFieldsValue({ content: val });
                                                            }}
                                                            relationId={item?.reviewId || 0}
                                                            type={EAIRichTextEditorType.AI_REVIEW_REPLY}
                                                            placeholder="Enter your reply here..."
                                                            height="250px"
                                                        />
                                                        <div className={s.replyFooter}>
                                                            {/* <div className={s.nameFoot}>
                                                                <div>Warm regards</div>
                                                                <div>
                                                                    {getProviderNameDisplay(
                                                                        user,
                                                                    )}
                                                                </div>
                                                            </div> */}
                                                            <div className={s.count}>
                                                                {textLength}/3000 characters
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            }}
                                        </Form.Item>
                                    </Form.Item>
                                </div>
                                {item?.isPublic === true && (
                                    <>
                                        <div className={s.checkWrap}>
                                            <Checkbox
                                                onChange={(e) => {
                                                    setShowOn(e.target.checked);
                                                }}
                                                checked={showOn}
                                            >
                                                Show my reply on helloklarity.com
                                            </Checkbox>
                                        </div>
                                        <div
                                            className={`${s.checkTips} ${
                                                showOn ? s.checkTipsHighLight : ''
                                            }`}
                                        >
                                            Please do not include any personal or health
                                            information about the patient in your reply.
                                        </div>
                                    </>
                                )}
                                {!item?.isAnonymous && (
                                    <div className={s.checkWrap}>
                                        <Checkbox
                                            onChange={(e) => {
                                                setSendAsDirectMessage(
                                                    e.target.checked,
                                                );
                                            }}
                                            checked={sendAsDirectMessage}
                                        >
                                            Send this reply as a direct message to
                                            the patient
                                        </Checkbox>
                                    </div>
                                )}
                            </div>
                        </Form>
                    </div>
                    <div className={s.right}>
                        <div className={s.tips}>
                            <div className={s.title}>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                >
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M18 10C18 14.4183 14.4183 18 10 18C5.58172 18 2 14.4183 2 10C2 5.58172 5.58172 2 10 2C14.4183 2 18 5.58172 18 10ZM11 6C11 6.55228 10.5523 7 10 7C9.44772 7 9 6.55228 9 6C9 5.44772 9.44772 5 10 5C10.5523 5 11 5.44772 11 6ZM9 9C8.44772 9 8 9.44772 8 10C8 10.5523 8.44772 11 9 11V14C9 14.5523 9.44772 15 10 15H11C11.5523 15 12 14.5523 12 14C12 13.4477 11.5523 13 11 13V10C11 9.44772 10.5523 9 10 9H9Z"
                                        fill="#60A5FA"
                                    />
                                </svg>
                                Pro standards for public replies
                            </div>
                            <ul>
                                <li>
                                    <b>Protect privacy.</b> Don&apos;t mention
                                    diagnoses, medications, visit details, or
                                    confirm someone is a patient beyond what
                                    they posted. Use general language (e.g.,
                                    &quot;your visit,&quot; &quot;your
                                    experience&quot;).
                                </li>
                                <li>
                                    <b>Lead with empathy, not defensiveness.</b>{' '}
                                    Always acknowledge the feeling first.
                                </li>
                                <li>
                                    <b>Own what you can control.</b> If the
                                    issue is service, access, timing, or
                                    communication, take responsibility and state
                                    what you&apos;ll do next.
                                </li>
                                <li>
                                    <b>Reaffirm clinical standards.</b>{' '}
                                    It&apos;s OK to say you prescribe or
                                    recommend treatments only when clinically
                                    appropriate and safe—patient safety first.
                                </li>
                                <li>
                                    <b>Offer a clear next step + timeline.</b>{' '}
                                    Give one secure channel and a response
                                    window (e.g., &quot;within one business
                                    day&quot;).
                                </li>
                                <li>
                                    <b>Keep it brief and calm.</b> Aim for
                                    60–120 words; two short paragraphs maximum.
                                    No exclamation points, no legalese.
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </Modal>
            <Modal
                open={showPhiLeakageModal}
                width="660px"
                onCancel={() => {
                    setShowPhiLeakageModal(false);
                }}
                footer={
                    <div className={s.footerWrap}>
                        <Button
                            onClick={() => {
                                setShowPhiLeakageModal(false);
                                onSubmit(true, true);
                            }}
                            size="large"
                            danger
                            type="primary"
                            className={s.btn}
                        >
                            Send privately
                        </Button>
                        <Button
                            onClick={() => {
                                setShowPhiLeakageModal(false);
                            }}
                            size="large"
                            type="primary"
                            className={s.btn}
                        >
                            Back to edit
                        </Button>
                    </div>
                }
            >
                <div className={s.phiWrap}>
                    <div className={s.phiLeft}>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                        >
                            <path
                                d="M11.9995 9V11M11.9995 15H12.0095M5.07134 19H18.9277C20.4673 19 21.4296 17.3333 20.6598 16L13.7316 4C12.9618 2.66667 11.0373 2.66667 10.2675 4L3.33929 16C2.56949 17.3333 3.53174 19 5.07134 19Z"
                                stroke="#DC2626"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </div>
                    <div className={s.phiRight}>
                        <div className={s.phiTitle}>Privacy reminder</div>
                        <div className={s.phiContent}>
                            Your reply may contain protected health information
                            (PHI). To protect patient privacy, please remove any
                            personal details (e.g., names, dates, contact info)
                            before posting publicly on helloklarity.com. You may
                            also choose to keep your reply private. Please check
                            your reply carefully. Sharing protected health
                            information (PHI) is a violation of our Terms of
                            Service and could result in your account being
                            suspended.
                        </div>
                    </div>
                </div>
            </Modal>
            <Modal
                open={showInappropriateContentModal}
                width="660px"
                onCancel={() => {
                    setInappropriateContentModal(false);
                }}
                footer={
                    <div className={s.footerWrap}>
                        <Button
                            onClick={() => {
                                setInappropriateContentModal(false);
                            }}
                            size="large"
                            type="primary"
                            className={s.btn}
                        >
                            Back to edit
                        </Button>
                    </div>
                }
            >
                <div className={s.phiWrap}>
                    <div className={s.phiLeft}>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                        >
                            <path
                                d="M11.9995 9V11M11.9995 15H12.0095M5.07134 19H18.9277C20.4673 19 21.4296 17.3333 20.6598 16L13.7316 4C12.9618 2.66667 11.0373 2.66667 10.2675 4L3.33929 16C2.56949 17.3333 3.53174 19 5.07134 19Z"
                                stroke="#DC2626"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </div>
                    <div className={s.phiRight}>
                        <div className={s.phiTitle}>Inappropriate content</div>
                        <div className={s.phiContent}>
                            Your response may contain unprofessional or
                            inappropriate language. Please review and edit your
                            response to ensure it adheres to our guidelines.
                            Maintaining a professional and respectful
                            communication environment is important for patient
                            trust and quality care.
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default ReplyReviewModal;
